import { useEffect, useState } from "react"
import useFetch from "../../../_fetch"
import { useAuth } from "../../../auth/auth-context"
import { ICustomAlert, IDuplicateConversions, IFeed_Alert } from "../../dashboard/alerts/alerts"
import { useAppContext } from "../../../AppContext"
import { format } from 'date-fns'
import { renderCustom, renderDuplicate, renderFeed } from "./render"
import { prepareChunks } from "./prepare-chunks"
import PageNumber from "../../dashboard/_page-number/page-number"
import { PageNumberProvider } from "../../dashboard/_page-number/page-number-context"

const isDev = process.env.NODE_ENV === 'development'

function ConditionalPageProvider({
  isIncluded,
  children,
}: {
  isIncluded: boolean
  children: React.ReactNode
}) {
  return !isIncluded ? <PageNumberProvider>{children}</PageNumberProvider> : <>{children}</>
}
export default function AlertReport(opts: { isIncluded?: boolean }) {
    const { doFetch } = useFetch()
    const [data, setData] = useState<{ duplicateOrderRefs: IDuplicateConversions, feeds: IFeed_Alert[], customAlerts: ICustomAlert[] } | undefined>()
    const { user } = useAuth()
    const appContext = useAppContext() 
    const origin = isDev ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com'
    const imgPath = origin + '/logo' + (user?.username ? '/' + user?.username : '')
    const alertIds = appContext.alertIds || []

    const getFromDate = () => {
        if(appContext.fromDate) return new Date(appContext.fromDate)

        const fromDate = new Date()
        fromDate.setDate(fromDate.getDate() - 7)
        fromDate.setHours(0, 0, 0, 0)
        return fromDate
    }
    const getToDate = () => {
        const date = appContext.toDate ? new Date(appContext.toDate) : new Date()

        date.setHours(23, 59, 59, 999)
        return date
    }

    const fromDate = getFromDate()
    const toDate = getToDate()


    const formatDateRange = (startDate: Date, endDate: Date): string => {
        return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
    }

    const filteredDuplicates: IDuplicateConversions = {}
    if((!alertIds.length || alertIds.includes('duplicate')) && data) {
        for(const orderRef in data?.duplicateOrderRefs) {
            if(orderRef === '') continue
            const sortedConversions = [...data.duplicateOrderRefs[orderRef]].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            const newest = sortedConversions[sortedConversions.length - 1]
            const isInDateRange = newest && new Date(newest.date) >= fromDate && new Date(newest.date) <= toDate
            if(isInDateRange) filteredDuplicates[orderRef] = data.duplicateOrderRefs[orderRef]
        }
    }

    const filteredFeeds:IFeed_Alert[] = []
    if(!alertIds.length || alertIds.includes('feed')) {
        for(const feed of data?.feeds || []) {
            const feedDate = new Date(feed.date)
            if(feedDate >= fromDate && feedDate <= toDate) filteredFeeds.push(feed)
        }
    }
    

    const filteredCustom = (data?.customAlerts || []).filter((alert) => {
        const alertDate = new Date(alert.date)
        if(alertIds.length && !alertIds.includes(alert.id)) return false

        return alertDate >= fromDate && alertDate <= toDate
    })


    const chunks = prepareChunks(filteredDuplicates, filteredFeeds, filteredCustom)

    const getTotalAlerts = () => {
        let total = 0
        for(const chunk of chunks) {
            total += chunk.length
        }
        return total
    }
    ;(window as any).getTotalAlerts = () => {
        return getTotalAlerts()
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await doFetch("/alerts/get-alerts")
                const json: { duplicateOrderRefs: IDuplicateConversions, feeds: IFeed_Alert[], customAlerts: ICustomAlert[] } = await response.json()
                setData(json)
            } catch (err) {
                console.error(err)
            }
        }
        fetchData()
    }, [])

    return (
        <ConditionalPageProvider isIncluded={!!opts.isIncluded}>
            {data && getTotalAlerts() === 0 ? (
                <div className="" style={{ width: '210mm', height: '297mm', position: 'relative',  }}>
                    <PageNumber />
                    <div className="px-3">
                        <div>
                            <div className="d-flex pt-3">
                                <h3>{appContext.reportName || 'Alert report'}</h3>
                                <img className="ms-auto" height={"38px"} src={imgPath} />
                            </div>
                            <h5 className="mb-3">{formatDateRange(fromDate, toDate)}</h5> 
                            <hr />
                        </div>
                        <h4>No alerts during this period</h4>
                        <p className="text-muted">
                            There have been no alerts in this date range.
                        </p>
                    </div>
                </div>
                
            ) : (
                <div className="px-3" style={{width:'210mm', height:`${chunks.length * 297}mm`}}>
                        {chunks.map((pageItems, pageIndex) => (
                            <div key={pageIndex} style={{position: 'relative', height: '297mm'}}>
                                <PageNumber />
                            <div className={ pageIndex === 0 ? '' : "page-break"}>
                                <div>
                                    <div className="d-flex pt-3">
                                        <h3>{appContext.reportName || 'Alert report'}</h3>
                                        <img className="ms-auto" height={"38px"} src={imgPath} />
                                    </div>
                                    <h5 className="mb-3">{formatDateRange(fromDate, toDate)}</h5> 
                                    <hr />
                                </div>
                                {pageItems.map((item, idx) => {
                                    const prev = pageItems[idx-1]
                                    switch (item.type) {
                                        case 'duplicate': return renderDuplicate(item.data[0], item.data[1], user?.settings.currency || '', prev?.type !== 'duplicate')
                                        case 'feed': return renderFeed(item.data, `${pageIndex}-${idx}`, prev?.type !== 'feed')
                                        case 'custom': return renderCustom(item.data, `${pageIndex}-${idx}`, prev?.type !== 'custom')
                                    }
                                })}
                            </div>
                            </div>
                        ))}
                </div>
            )}
        </ConditionalPageProvider>
    )
}